/* eslint-disable no-unused-vars */

import React from 'react';
import {
    formatDateShort, formatUSDCurrency, formatDecimalAsPercentage, numeralFormatter,
} from 'ns_libs/formatter';
import { URLS } from 'services/urls';
import { generatePath, Link } from 'react-router-dom';
import ProjectActualsCell from './DealsTable/ProjectActualsCell';
import { Deal, IOptionStringValue } from './types';

export enum PROJECT_ACTUALS_TYPE {
    IRR = 'irr',
    EQUITY_MULTIPLE = 'equityMultiple'
}

export enum TABLE_KEYS {
    all = 'all',
    // General
    deal = 'dealName',
    address = 'address',
    city = 'city',
    county = 'county',
    state = 'state',
    zipCode = 'zipCode',
    market = 'market',
    submarket = 'submarket',
    dateCreated = 'createdAt',
    dateUpdated = 'updatedAt',
    description = 'description',

    // Deal info
    dealType = 'dealType.name',
    assetType = 'assetType.name',
    constructionType = 'constructionType.name',
    parkingType = 'parkingType',
    units = 'units',
    subprojectStage = 'subprojectStage.name',
    leadSource = 'leadSource.name',
    leasingStrategy = 'leasingStrategy.name',
    rank = 'rank.name',

    // Deal metrics
    acquisitionPrice = 'acquisitionPrice',
    targetROC = 'targetReturnOnCost',
    targetROE = 'targetReturnOnEquity',
    targetPR = 'targetProfitReturn',
    irr = 'irr',
    equityMultiple = 'equityMultiple',
    irrProjectActuals = 'irrProjectActuals',
    equityMultipleProjectActuals = 'equityMultipleProjectActuals',

    // Personnel
    createdBy = 'creatorName',
    dealOwner = 'dealOwner.name',
    developmentManager = 'developmentManager.name',

    // Capital partners
    investor = 'investorLp.name',
    lender = 'lenderPrivate.name',

    // Vendors
    legalServices = 'legalServices.name',
    projectArchitect = 'projectArchitect.name',
    surveyor = 'surveyor.name',
    environmentalConsultant = 'environmentalConsultant.name',
}

export const UNIT_TYPES = {
    GROSS_BUILD_SF: 'grossBuildSf',
    RENTABLE_SF: 'rentableSf',
    ACRES: 'acres',
    RESIDENTIAL_UNITS: 'residentialUnits',
    LOTS: 'lots',
    BEDS: 'beds',
    MEGAWATTS: 'megawatts',
};

export const UNIT_TYPE_ABBREVIATIONS = {
    [UNIT_TYPES.GROSS_BUILD_SF]: 'G. SF',
    [UNIT_TYPES.RENTABLE_SF]: 'R. SF',
    [UNIT_TYPES.ACRES]: 'ACRE',
    [UNIT_TYPES.RESIDENTIAL_UNITS]: 'UNIT',
    [UNIT_TYPES.LOTS]: 'LOT',
    [UNIT_TYPES.BEDS]: 'BED',
    [UNIT_TYPES.MEGAWATTS]: 'MW',
};

export type UnitType = (typeof UNIT_TYPES)[keyof typeof UNIT_TYPES];

export const UNIT_TYPE_LABELS = {
    [UNIT_TYPES.GROSS_BUILD_SF]: 'Built gross SF',
    [UNIT_TYPES.RENTABLE_SF]: 'Rentable SF',
    [UNIT_TYPES.ACRES]: 'Acres',
    [UNIT_TYPES.RESIDENTIAL_UNITS]: 'Residential units',
    [UNIT_TYPES.LOTS]: 'Lots',
    [UNIT_TYPES.BEDS]: 'Beds',
    [UNIT_TYPES.MEGAWATTS]: 'Megawatts',
};

export const COLUMN_OPTIONS: Array<
    IOptionStringValue & {
        sortable?: boolean;
        cellRenderer?: (row: Deal) => JSX.Element;
        includeDropdown?: boolean;
        dropdownOptions?: IOptionStringValue[];
        alignRight?: boolean;
    }
> = [
    // General
    {
        label: 'Deal',
        value: TABLE_KEYS.deal,
        sortable: true,
        cellRenderer: row => <Link to={generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId: row.id || '' })}>{row.dealName}</Link>,
    },
    { label: 'Address', value: TABLE_KEYS.address, sortable: true },
    { label: 'City', value: TABLE_KEYS.city, sortable: true },
    { label: 'County', value: TABLE_KEYS.county, sortable: true },
    { label: 'State', value: TABLE_KEYS.state, sortable: true },
    { label: 'Zip code', value: TABLE_KEYS.zipCode, sortable: true },
    { label: 'Market', value: TABLE_KEYS.market, sortable: true },
    { label: 'Submarket', value: TABLE_KEYS.submarket, sortable: true },
    {
        label: 'Date created', value: TABLE_KEYS.dateCreated, sortable: true, cellRenderer: row => <span>{formatDateShort(row.createdAt)}</span>,
    },
    {
        label: 'Date updated', value: TABLE_KEYS.dateUpdated, sortable: true, cellRenderer: row => <span>{formatDateShort(row.updatedAt)}</span>,
    },
    { label: 'Description', value: TABLE_KEYS.description, sortable: true },

    // Deal info
    { label: 'Deal type', value: TABLE_KEYS.dealType, sortable: true },
    { label: 'Asset type', value: TABLE_KEYS.assetType, sortable: true },
    { label: 'Construction type', value: TABLE_KEYS.constructionType, sortable: true },
    { label: 'Parking type', value: TABLE_KEYS.parkingType, sortable: true },
    {
        label: 'Units',
        value: TABLE_KEYS.units,
        sortable: true,
        alignRight: true,
        includeDropdown: true,
        dropdownOptions: [
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.GROSS_BUILD_SF], value: UNIT_TYPES.GROSS_BUILD_SF },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.RENTABLE_SF], value: UNIT_TYPES.RENTABLE_SF },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.ACRES], value: UNIT_TYPES.ACRES },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.RESIDENTIAL_UNITS], value: UNIT_TYPES.RESIDENTIAL_UNITS },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.LOTS], value: UNIT_TYPES.LOTS },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.BEDS], value: UNIT_TYPES.BEDS },
            { label: UNIT_TYPE_LABELS[UNIT_TYPES.MEGAWATTS], value: UNIT_TYPES.MEGAWATTS },
        ],
    },
    { label: 'Phase', value: TABLE_KEYS.subprojectStage, sortable: true },
    { label: 'Lead source', value: TABLE_KEYS.leadSource, sortable: true },
    { label: 'Leasing strategy', value: TABLE_KEYS.leasingStrategy, sortable: true },
    { label: 'Rank', value: TABLE_KEYS.rank, sortable: true },

    // Personnel
    {
        label: 'Created by',
        value: TABLE_KEYS.createdBy,
        sortable: true,
        cellRenderer: row => (row.acceptedByName ? <span>{row.acceptedByName}</span> : <span>{row.creatorName}</span>),
    },
    { label: 'Deal owner', value: TABLE_KEYS.dealOwner, sortable: true },
    { label: 'Development manager', value: TABLE_KEYS.developmentManager, sortable: true },

    // Deal metrics
    {
        label: 'Acquisition price',
        value: TABLE_KEYS.acquisitionPrice,
        sortable: true,
        alignRight: true,
        cellRenderer: row => <div style={{ textAlign: 'right' }}>{formatUSDCurrency(row.acquisitionPrice, true)}</div>,
    },
    { label: 'Target return on cost', value: TABLE_KEYS.targetROC, sortable: true },
    { label: 'Target return on equity', value: TABLE_KEYS.targetROE, sortable: true },
    { label: 'Target profit return', value: TABLE_KEYS.targetPR, sortable: true },
    {
        label: 'IRR',
        tooltip: 'Coming soon',
        disabled: true,
        value: TABLE_KEYS.irr,
        sortable: true,
        alignRight: true,
        cellRenderer: row => {
            const { irr } = row;
            if (irr == null || irr === undefined) return <span>-</span>;
            return <span>{formatDecimalAsPercentage(irr, 2)}</span>;
        },
    },
    {
        label: 'IRR (using project actuals)',
        tooltip: 'Coming soon',
        disabled: true,
        value: TABLE_KEYS.irrProjectActuals,
        sortable: false,
        alignRight: true,
        cellRenderer: row => <ProjectActualsCell dealId={row.id} type={PROJECT_ACTUALS_TYPE.IRR} />,
    },
    {
        label: 'Equity multiple',
        tooltip: 'Coming soon',
        disabled: true,
        value: TABLE_KEYS.equityMultiple,
        sortable: true,
        alignRight: true,
        cellRenderer: row => {
            const { equityMultiple } = row;
            if (equityMultiple == null || equityMultiple === undefined) return <span>-</span>;
            return (
                <span>
                    {numeralFormatter(equityMultiple, '0.00')}
                    x
                </span>
            );
        },
    },
    {
        label: 'Equity multiple (using project actuals)',
        tooltip: 'Coming soon',
        disabled: true,
        value: TABLE_KEYS.equityMultipleProjectActuals,
        sortable: false,
        alignRight: true,
        cellRenderer: row => <ProjectActualsCell dealId={row.id} type={PROJECT_ACTUALS_TYPE.EQUITY_MULTIPLE} />,
    },

    // Capital partners
    { label: 'Investor - LP', value: TABLE_KEYS.investor, sortable: true },
    { label: 'Lender (private)', value: TABLE_KEYS.lender, sortable: true },

    // Vendors
    { label: 'Legal services', value: TABLE_KEYS.legalServices, sortable: true },
    { label: 'Project architect', value: TABLE_KEYS.projectArchitect, sortable: true },
    { label: 'Surveyor', value: TABLE_KEYS.surveyor, sortable: true },
    { label: 'Environmental consultant', value: TABLE_KEYS.environmentalConsultant, sortable: true },
];

const INITIAL_VISIBLE_COLUMNS: Partial<Record<TABLE_KEYS, boolean>> = {
    [TABLE_KEYS.deal]: true,
    [TABLE_KEYS.address]: true,
    [TABLE_KEYS.dateCreated]: true,
    [TABLE_KEYS.acquisitionPrice]: true,
    [TABLE_KEYS.createdBy]: true,
};

export const getInitialColumnVisibility = (): Record<string, boolean> => {
    const defaultVisibility: Record<string, boolean> = {};

    COLUMN_OPTIONS.forEach(column => {
        defaultVisibility[column.value] = INITIAL_VISIBLE_COLUMNS[column.value as TABLE_KEYS] ?? false;
    });

    return defaultVisibility;
};

export enum DEVELOPMENT_MANAGER_ASSOCIATION_TYPE {
    CONTACT = 'contact',
    COMPANY = 'company',
    USER = 'user',
}

export const SELECT_COLUMN_ID = 'select';
export const ACTIONS_COLUMN_ID = 'actions';

// local storage keys
export const DEALS_ACTIVE_SELECTION = 'dealsActiveSection';
export const DEALS_TABLE_UNIT_TYPE = 'dealsTableUnitType';
export const IS_EXPANDED_PROJECT_PIPELINE = 'isExpandedProjectPipeline';
export const DEAL_PIPELINE_SELECTED_STAGES = 'dealPipelineSelectedStages';

export const DISABLED_PRO_FORMA_BTN_TOOLTIP = 'Please create a deal first';
